// src/components/vendor/VendorRegistrationPage.tsx
// DROP 18b — Public vendor/service provider registration
// No login required — open to all applicants

import React, { useState, useEffect } from "react";
import {
  Music, UtensilsCrossed, Camera, Shield, Truck,
  Stethoscope, Palette, Store, ChevronRight, ChevronLeft,
  CheckCircle, AlertTriangle, Upload, X, Loader2, Crown,
  Phone, Mail, MapPin, User, Briefcase, FileText, CreditCard
} from "lucide-react";

interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
  fee: number;
  currency: string;
  maxSlots: number;
  filledSlots: number;
  isOpen: boolean;
  criteria: string[];
  requiredDocs: string[];
}

interface FormData {
  categoryId: string;
  fullName: string;
  businessName: string;
  phone: string;
  email: string;
  state: string;
  lga: string;
  experience: string;
  description: string;
  reference1: string;
  reference2: string;
  idDocUrl: string;
  portfolioUrl: string;
  agreeTerms: boolean;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  dj:           <Music size={20}/>,
  food_vendor:  <UtensilsCrossed size={20}/>,
  canteen:      <Store size={20}/>,
  photographer: <Camera size={20}/>,
  security:     <Shield size={20}/>,
  logistics:    <Truck size={20}/>,
  medical:      <Stethoscope size={20}/>,
  craft:        <Palette size={20}/>,
  other:        <Briefcase size={20}/>,
};

const NIGERIAN_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","FCT","Gombe","Imo",
  "Jigawa","Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa",
  "Niger","Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba",
  "Yobe","Zamfara"
];

export default function VendorRegistrationPage() {
  const [categories,  setCategories]  = useState<Category[]>([]);
  const [step,        setStep]        = useState<1|2|3|4>(1);
  const [selected,    setSelected]    = useState<Category | null>(null);
  const [form,        setForm]        = useState<FormData>({
    categoryId:"", fullName:"", businessName:"", phone:"", email:"",
    state:"", lga:"", experience:"", description:"",
    reference1:"", reference2:"", idDocUrl:"", portfolioUrl:"",
    agreeTerms: false,
  });
  const [uploading,   setUploading]   = useState<"id"|"portfolio"|null>(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const [accredId,    setAccredId]    = useState("");
  const [errors,      setErrors]      = useState<Record<string,string>>({});
  const [payLoading,  setPayLoading]  = useState(false);

  useEffect(() => {
    fetch("/api/vendor/categories")
      .then(r => r.json())
      .then(d => { if (d.success) setCategories(d.categories); });
  }, []);

  // Load Paystack inline script
  useEffect(() => {
    if (!document.getElementById("paystack-script")) {
      const s = document.createElement("script");
      s.id = "paystack-script";
      s.src = "https://js.paystack.co/v1/inline.js";
      document.head.appendChild(s);
    }
  }, []);

  const upd = (field: keyof FormData, val: string | boolean) =>
    setForm(p => ({ ...p, [field]: val }));

  const validate2 = (): boolean => {
    const e: Record<string,string> = {};
    if (!form.fullName.trim())    e.fullName    = "Full name required";
    if (!form.phone.trim())       e.phone       = "Phone required";
    if (!form.email.trim())       e.email       = "Email required";
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.state)              e.state       = "State required";
    if (!form.experience.trim())  e.experience  = "Experience required";
    if (!form.description.trim()) e.description = "Description required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validate3 = (): boolean => {
    const e: Record<string,string> = {};
    if (!form.agreeTerms) e.agreeTerms = "You must agree to the terms";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const uploadFile = async (file: File, type: "id"|"portfolio") => {
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "vendor-docs");
      const res = await fetch("/api/community-portals/upload", { method:"POST", body:fd });
      const d = await res.json();
      if (d.success && d.url) {
        upd(type === "id" ? "idDocUrl" : "portfolioUrl", d.url);
      }
    } catch { /* silent */ }
    finally { setUploading(null); }
  };

  const initiatePayment = async () => {
    if (!validate3()) return;
    setPayLoading(true);
    try {
      // Get public key + create pending application
      const initRes = await fetch("/api/vendor/initiate-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, categoryId: selected!.id })
      });
      const initData = await initRes.json();
      if (!initData.success) {
        setErrors({ submit: initData.error || "Failed to initiate payment" });
        setPayLoading(false); return;
      }

      const { reference, publicKey, amount, email } = initData;

      // Paystack inline
      const handler = (window as any).PaystackPop?.setup({
        key: publicKey,
        email: email || form.email,
        amount: amount * 100, // kobo
        currency: selected!.currency || "NGN",
        ref: reference,
        metadata: {
          payment_type: "vendor_registration",
          vendor_name: form.fullName,
          category: selected!.name,
          application_id: initData.applicationId,
        },
        callback: async (response: { reference: string }) => {
          // Verify payment
          const verRes = await fetch("/api/vendor/verify-payment", {
            method: "POST",
            headers: { "Content-Type":"application/json" },
            body: JSON.stringify({ reference: response.reference, applicationId: initData.applicationId })
          });
          const verData = await verRes.json();
          if (verData.success) {
            setAccredId(verData.applicationId || initData.applicationId);
            setSubmitted(true); setStep(4);
          } else {
            setErrors({ submit: "Payment verification failed — contact admin" });
          }
        },
        onClose: () => { setPayLoading(false); }
      });
      handler?.openIframe();
    } catch (e) {
      setErrors({ submit: "Payment system error — please try again" });
    } finally { setPayLoading(false); }
  };

  const fmt = (amount: number, currency = "NGN") =>
    new Intl.NumberFormat("en-NG", { style:"currency", currency }).format(amount);

  // ── STEP 1: Category selection ──────────────────────────────────
  if (step === 1) return (
    <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center border-2"
                 style={{ borderColor:"#C9A84C", background:"rgba(201,168,76,0.1)" }}>
              <Crown size={24} style={{ color:"#C9A84C" }}/>
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Service Provider Registration
          </h1>
          <p className="text-sm mb-1" style={{ color:"rgba(237,232,220,0.5)" }}>
            ERUE Festival of Uzairue Kingdom · December 24–30, 2026
          </p>
          <p className="text-xs" style={{ color:"rgba(237,232,220,0.3)" }}>
            Register as an accredited service provider for the festival
          </p>
        </div>

        {/* Step indicator */}
        <StepBar step={1}/>

        <h2 className="text-lg font-semibold mb-4 mt-8" style={{ color:"#EDE8DC" }}>
          Select Your Service Category
        </h2>

        {categories.length === 0 ? (
          <div className="text-center py-16" style={{ color:"rgba(237,232,220,0.3)" }}>
            <Loader2 size={32} className="animate-spin mx-auto mb-3"/>
            Loading categories…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(cat => (
              <button key={cat.id} disabled={!cat.isOpen}
                      onClick={() => { setSelected(cat); upd("categoryId", cat.id); setStep(2); }}
                      className="text-left p-5 rounded-2xl border transition-all group"
                      style={{
                        borderColor: cat.isOpen ? "#1C2E22" : "rgba(28,46,34,0.4)",
                        background: cat.isOpen ? "#0E1812" : "rgba(14,24,18,0.4)",
                        opacity: cat.isOpen ? 1 : 0.5,
                        cursor: cat.isOpen ? "pointer" : "not-allowed",
                      }}
                      onMouseEnter={e => cat.isOpen && ((e.currentTarget as HTMLElement).style.borderColor = "rgba(201,168,76,0.4)")}
                      onMouseLeave={e => cat.isOpen && ((e.currentTarget as HTMLElement).style.borderColor = "#1C2E22")}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                       style={{ background:"rgba(201,168,76,0.1)", color:"#C9A84C" }}>
                    {CATEGORY_ICONS[cat.id] || <Briefcase size={20}/>}
                  </div>
                  {!cat.isOpen && (
                    <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background:"rgba(239,68,68,0.15)", color:"#FCA5A5", border:"1px solid rgba(239,68,68,0.2)" }}>
                      Closed
                    </span>
                  )}
                  {cat.maxSlots > 0 && cat.isOpen && (
                    <span className="text-xs" style={{ color:"rgba(237,232,220,0.3)" }}>
                      {cat.filledSlots}/{cat.maxSlots} slots
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-sm mb-1" style={{ color:"#EDE8DC" }}>{cat.name}</h3>
                <p className="text-xs mb-3" style={{ color:"rgba(237,232,220,0.4)" }}>{cat.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm" style={{ color:"#C9A84C" }}>
                    {fmt(cat.fee, cat.currency)}
                  </span>
                  {cat.isOpen && (
                    <ChevronRight size={16} style={{ color:"rgba(201,168,76,0.5)" }}/>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── STEP 2: Application form ─────────────────────────────────────
  if (step === 2 && selected) return (
    <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-1" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            {selected.name} Application
          </h1>
          <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
            Registration Fee: <span style={{ color:"#C9A84C" }}>{fmt(selected.fee, selected.currency)}</span>
          </p>
        </div>

        <StepBar step={2}/>

        {/* Criteria box */}
        {selected.criteria.length > 0 && (
          <div className="mb-6 p-4 rounded-xl" style={{ background:"rgba(201,168,76,0.05)", border:"1px solid rgba(201,168,76,0.2)" }}>
            <p className="text-xs font-semibold mb-2" style={{ color:"#C9A84C" }}>
              PREQUALIFICATION CRITERIA
            </p>
            <ul className="space-y-1">
              {selected.criteria.map((c,i) => (
                <li key={i} className="flex items-start gap-2 text-xs" style={{ color:"rgba(237,232,220,0.7)" }}>
                  <CheckCircle size={12} className="mt-0.5 flex-shrink-0" style={{ color:"#C9A84C" }}/> {c}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-4" style={{ background:"#0E1812", border:"1px solid #1C2E22", borderRadius:"16px", padding:"24px" }}>
          <Section title="Personal Information" icon={<User size={14}/>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full Name *" error={errors.fullName}>
                <input value={form.fullName} onChange={e=>upd("fullName",e.target.value)}
                       placeholder="Your full legal name"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:`1px solid ${errors.fullName?"rgba(239,68,68,0.5)":"#1C2E22"}`, color:"#EDE8DC" }}/>
              </Field>
              <Field label="Business / Trade Name">
                <input value={form.businessName} onChange={e=>upd("businessName",e.target.value)}
                       placeholder="Business name (if any)"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Field label="Phone *" error={errors.phone}>
                <input value={form.phone} onChange={e=>upd("phone",e.target.value)}
                       placeholder="+234 000 000 0000" type="tel"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:`1px solid ${errors.phone?"rgba(239,68,68,0.5)":"#1C2E22"}`, color:"#EDE8DC" }}/>
              </Field>
              <Field label="Email *" error={errors.email}>
                <input value={form.email} onChange={e=>upd("email",e.target.value)}
                       placeholder="your@email.com" type="email"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:`1px solid ${errors.email?"rgba(239,68,68,0.5)":"#1C2E22"}`, color:"#EDE8DC" }}/>
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Field label="State of Origin *" error={errors.state}>
                <select value={form.state} onChange={e=>upd("state",e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                        style={{ background:"#121E16", border:`1px solid ${errors.state?"rgba(239,68,68,0.5)":"#1C2E22"}`, color: form.state?"#EDE8DC":"rgba(237,232,220,0.3)" }}>
                  <option value="">Select state</option>
                  {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="LGA">
                <input value={form.lga} onChange={e=>upd("lga",e.target.value)}
                       placeholder="Local Government Area"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </Field>
            </div>
          </Section>

          <Section title="Service Details" icon={<Briefcase size={14}/>}>
            <Field label="Years of Experience *" error={errors.experience}>
              <select value={form.experience} onChange={e=>upd("experience",e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                      style={{ background:"#121E16", border:`1px solid ${errors.experience?"rgba(239,68,68,0.5)":"#1C2E22"}`, color: form.experience?"#EDE8DC":"rgba(237,232,220,0.3)" }}>
                <option value="">Select experience</option>
                <option value="less_1">Less than 1 year</option>
                <option value="1_3">1 – 3 years</option>
                <option value="3_5">3 – 5 years</option>
                <option value="5_10">5 – 10 years</option>
                <option value="10_plus">10+ years</option>
              </select>
            </Field>
            <Field label="Services / Equipment Description *" error={errors.description} cls="mt-3">
              <textarea value={form.description} onChange={e=>upd("description",e.target.value)}
                        rows={4} placeholder="Describe your services, equipment, capacity, specialties…"
                        className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
                        style={{ background:"#121E16", border:`1px solid ${errors.description?"rgba(239,68,68,0.5)":"#1C2E22"}`, color:"#EDE8DC" }}/>
            </Field>
          </Section>

          <Section title="References (Optional)" icon={<FileText size={14}/>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Reference 1 (Name & Phone)">
                <input value={form.reference1} onChange={e=>upd("reference1",e.target.value)}
                       placeholder="Full name + phone number"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </Field>
              <Field label="Reference 2 (Name & Phone)">
                <input value={form.reference2} onChange={e=>upd("reference2",e.target.value)}
                       placeholder="Full name + phone number"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
              </Field>
            </div>
          </Section>

          {selected.requiredDocs.length > 0 && (
            <Section title="Document Uploads" icon={<Upload size={14}/>}>
              <div className="space-y-3">
                <div>
                  <p className="text-xs mb-2" style={{ color:"rgba(237,232,220,0.5)" }}>
                    Valid ID (NIN, Driver's License, Passport, Voter's Card)
                  </p>
                  {form.idDocUrl ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle size={14} style={{ color:"#4ADE80" }}/>
                      <span className="text-xs" style={{ color:"#4ADE80" }}>ID uploaded</span>
                      <button onClick={() => upd("idDocUrl","")} className="ml-2"
                              style={{ color:"rgba(237,232,220,0.3)" }}><X size={12}/></button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer border transition-all w-fit"
                           style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
                      {uploading==="id" ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
                      <span className="text-xs">{uploading==="id"?"Uploading…":"Upload ID Document"}</span>
                      <input type="file" className="hidden" accept="image/*,.pdf"
                             onChange={e=>e.target.files?.[0]&&uploadFile(e.target.files[0],"id")}/>
                    </label>
                  )}
                </div>
                <div>
                  <p className="text-xs mb-2" style={{ color:"rgba(237,232,220,0.5)" }}>
                    Portfolio / Work samples (photos, certificate, etc.)
                  </p>
                  {form.portfolioUrl ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle size={14} style={{ color:"#4ADE80" }}/>
                      <span className="text-xs" style={{ color:"#4ADE80" }}>Portfolio uploaded</span>
                      <button onClick={() => upd("portfolioUrl","")} className="ml-2"
                              style={{ color:"rgba(237,232,220,0.3)" }}><X size={12}/></button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer border transition-all w-fit"
                           style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
                      {uploading==="portfolio"?<Loader2 size={14} className="animate-spin"/>:<Upload size={14}/>}
                      <span className="text-xs">{uploading==="portfolio"?"Uploading…":"Upload Portfolio"}</span>
                      <input type="file" className="hidden" accept="image/*,.pdf"
                             onChange={e=>e.target.files?.[0]&&uploadFile(e.target.files[0],"portfolio")}/>
                    </label>
                  )}
                </div>
              </div>
            </Section>
          )}

          <div className="flex gap-3 mt-4">
            <button onClick={() => setStep(1)}
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm border"
                    style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
              <ChevronLeft size={14}/> Back
            </button>
            <button onClick={() => { if (validate2()) setStep(3); }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background:"#C9A84C", color:"#070D0A" }}>
              Review & Pay <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── STEP 3: Review + Pay ─────────────────────────────────────────
  if (step === 3 && selected) return (
    <div className="min-h-screen py-12 px-4" style={{ background:"#070D0A" }}>
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-1" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
            Review & Pay
          </h1>
        </div>

        <StepBar step={3}/>

        <div className="mt-8 space-y-4">
          {/* Summary */}
          <div className="p-5 rounded-2xl" style={{ background:"#0E1812", border:"1px solid #1C2E22" }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color:"#C9A84C" }}>Application Summary</h3>
            <div className="space-y-2">
              {[
                ["Category",      selected.name],
                ["Full Name",     form.fullName],
                ["Business Name", form.businessName || "—"],
                ["Phone",         form.phone],
                ["Email",         form.email],
                ["State",         form.state || "—"],
                ["Experience",    form.experience.replace(/_/g," ")],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between text-sm">
                  <span style={{ color:"rgba(237,232,220,0.4)" }}>{label}</span>
                  <span style={{ color:"#EDE8DC" }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment */}
          <div className="p-5 rounded-2xl" style={{ background:"rgba(201,168,76,0.05)", border:"1px solid rgba(201,168,76,0.2)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard size={16} style={{ color:"#C9A84C" }}/>
                <span className="text-sm font-semibold" style={{ color:"#EDE8DC" }}>Registration Fee</span>
              </div>
              <span className="text-xl font-bold" style={{ color:"#C9A84C" }}>
                {fmt(selected.fee, selected.currency)}
              </span>
            </div>
            <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
              Payment is processed securely via Paystack. Your application will be submitted to the
              festival committee for review upon successful payment.
            </p>
          </div>

          {/* Terms */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.agreeTerms}
                   onChange={e => upd("agreeTerms", e.target.checked)}
                   className="mt-0.5 accent-yellow-400"/>
            <span className="text-xs" style={{ color:"rgba(237,232,220,0.6)" }}>
              I confirm that all information provided is accurate and I agree to abide by the
              ERUE Festival terms and conditions for service providers. I understand that
              accreditation is subject to approval by the festival committee.
            </span>
          </label>
          {errors.agreeTerms && (
            <p className="text-xs" style={{ color:"#FCA5A5" }}>⚠ {errors.agreeTerms}</p>
          )}

          {errors.submit && (
            <div className="p-3 rounded-xl text-sm flex items-center gap-2"
                 style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", color:"#FCA5A5" }}>
              <AlertTriangle size={14}/> {errors.submit}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep(2)}
                    className="flex items-center gap-1.5 px-5 py-3 rounded-xl text-sm border"
                    style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>
              <ChevronLeft size={14}/> Back
            </button>
            <button onClick={initiatePayment} disabled={payLoading || submitting}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold"
                    style={{ background: payLoading?"rgba(201,168,76,0.4)":"#C9A84C", color:"#070D0A" }}>
              {payLoading ? <><Loader2 size={16} className="animate-spin"/> Processing…</> :
                           <><CreditCard size={16}/> Pay {fmt(selected.fee, selected.currency)}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── STEP 4: Confirmation ─────────────────────────────────────────
  if (step === 4 || submitted) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background:"#070D0A" }}>
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
             style={{ background:"rgba(74,222,128,0.1)", border:"2px solid rgba(74,222,128,0.3)" }}>
          <CheckCircle size={36} style={{ color:"#4ADE80" }}/>
        </div>
        <h1 className="text-2xl font-bold mb-2" style={{ color:"#EDE8DC", fontFamily:"Cinzel, serif" }}>
          Application Submitted!
        </h1>
        <p className="text-sm mb-6" style={{ color:"rgba(237,232,220,0.5)" }}>
          Your registration fee has been received. Your application is now under review by the
          ERUE Festival committee.
        </p>
        <div className="p-5 rounded-2xl mb-6" style={{ background:"#0E1812", border:"1px solid #1C2E22" }}>
          <p className="text-xs mb-1" style={{ color:"rgba(237,232,220,0.4)" }}>Application Reference</p>
          <p className="text-lg font-bold font-mono" style={{ color:"#C9A84C" }}>{accredId}</p>
          <p className="text-xs mt-3" style={{ color:"rgba(237,232,220,0.4)" }}>
            Keep this reference number. You will be contacted via email/phone
            when your application is approved or if further information is required.
          </p>
        </div>
        <p className="text-xs" style={{ color:"rgba(237,232,220,0.3)" }}>
          Questions? Contact the festival secretariat at erueuzairue@gmail.com
        </p>
      </div>
    </div>
  );

  return null;
}

// ── Helper sub-components ──────────────────────────────────────────
function StepBar({ step }: { step: number }) {
  const steps = ["Select Category", "Application Form", "Review & Pay", "Confirmed"];
  return (
    <div className="flex items-center justify-center gap-0 mt-4">
      {steps.map((label, i) => {
        const n = i + 1;
        const done    = n < step;
        const current = n === step;
        return (
          <React.Fragment key={n}>
            <div className="flex flex-col items-center">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all"
                   style={{
                     background:  done ? "#C9A84C" : current ? "rgba(201,168,76,0.2)" : "transparent",
                     borderColor: done || current ? "#C9A84C" : "#1C2E22",
                     color:       done ? "#070D0A" : current ? "#C9A84C" : "rgba(237,232,220,0.3)",
                   }}>
                {done ? "✓" : n}
              </div>
              <span className="text-xs mt-1 hidden sm:block"
                    style={{ color: current ? "#C9A84C" : "rgba(237,232,220,0.3)" }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="w-8 sm:w-16 h-px mb-4 mx-1"
                   style={{ background: n < step ? "#C9A84C" : "#1C2E22" }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Section({ title, icon, children }: { title:string; icon:React.ReactNode; children:React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span style={{ color:"#C9A84C" }}>{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color:"#C9A84C" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Field({ label, error, children, cls="" }: { label:string; error?:string; children:React.ReactNode; cls?:string }) {
  return (
    <div className={cls}>
      <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.5)" }}>{label}</label>
      {children}
      {error && <p className="text-xs mt-1" style={{ color:"#FCA5A5" }}>⚠ {error}</p>}
    </div>
  );
}
